import React, { useState } from 'react';
import { ChevronRightIcon, ChevronLeftIcon, SparklesIcon } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
interface QuizAnswers {
  ages: string[];
  guestCount: string;
  spaceSize: string;
  lookPreference: string;
  aestheticImportance: string;
  colorTheme: string;
}
interface PackageRecommendation {
  name: string;
  description: string;
  reasons: string[];
  link: string;
  color: string;
  packageValue: string;
  bookingType: string;
  price: string;
}
export const PackageQuiz = () => {
  const navigate = useNavigate();
  const {
    addToCart
  } = useCart();
  const [isQuizOpen, setIsQuizOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<QuizAnswers>({
    ages: [],
    guestCount: '',
    spaceSize: '',
    lookPreference: '',
    aestheticImportance: '',
    colorTheme: ''
  });
  const [recommendation, setRecommendation] = useState<PackageRecommendation | null>(null);
  // REMOVED QUESTION 3 (eventType) and QUESTION 4 (location) - now only 6 questions
  const questions = [{
    id: 'ages',
    question: 'How old are the children who will be playing?',
    subtitle: 'Select all that apply',
    type: 'multiple',
    options: [{
      value: 'under2',
      label: 'Under 2'
    }, {
      value: '2-4',
      label: '2–4'
    }, {
      value: '5-7',
      label: '5–7'
    }, {
      value: '8-10',
      label: '8–10'
    }]
  }, {
    id: 'guestCount',
    question: 'About how many children will be playing?',
    type: 'single',
    options: [{
      value: '5-or-fewer',
      label: '5 or fewer'
    }, {
      value: '6-10',
      label: '6–10'
    }, {
      value: '11-15',
      label: '11–15'
    }, {
      value: '16-20',
      label: '16–20'
    }]
  }, {
    id: 'spaceSize',
    question: 'How much space do you have available for play?',
    type: 'single',
    options: [{
      value: 'small',
      label: 'Small space (living room, apartment, small party room)'
    }, {
      value: 'medium',
      label: 'Medium space (basement, backyard, standard venue)'
    }, {
      value: 'large',
      label: 'Large open space (event hall, gym, large yard)'
    }]
  }, {
    id: 'lookPreference',
    question: "Which statement best describes what you're looking for?",
    type: 'single',
    options: [{
      value: 'simple',
      label: 'Simple, fun, and cute'
    }, {
      value: 'variety',
      label: 'Enough space and activities for kids to rotate and stay busy'
    }, {
      value: 'big-energy',
      label: 'Big party energy with lots of kids playing at once'
    }, {
      value: 'luxury',
      label: 'A luxury, clean, elevated look'
    }]
  }, {
    id: 'aestheticImportance',
    question: 'How important is the look of the setup?',
    type: 'single',
    options: [{
      value: 'not-priority',
      label: 'Not a top priority'
    }, {
      value: 'somewhat',
      label: 'Somewhat important'
    }, {
      value: 'very',
      label: 'Very important – I want a luxury look'
    }]
  }, {
    id: 'colorTheme',
    question: 'Do you want the setup to match a specific color theme?',
    type: 'single',
    options: [{
      value: 'no',
      label: 'No'
    }, {
      value: 'yes',
      label: 'Yes'
    }]
  }];
  const handleMultipleChoice = (questionId: string, value: string) => {
    setAnswers(prev => {
      const currentValues = prev[questionId as keyof QuizAnswers] as string[];
      const newValues = currentValues.includes(value) ? currentValues.filter(v => v !== value) : [...currentValues, value];
      return {
        ...prev,
        [questionId]: newValues
      };
    });
  };
  const handleSingleChoice = (questionId: string, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };
  const canProceed = () => {
    const currentQuestion = questions[currentStep];
    const answer = answers[currentQuestion.id as keyof QuizAnswers];
    if (currentQuestion.type === 'multiple') {
      return Array.isArray(answer) && answer.length > 0;
    }
    return answer !== '';
  };
  const calculateRecommendation = (): PackageRecommendation => {
    const {
      guestCount,
      spaceSize,
      aestheticImportance,
      colorTheme,
      lookPreference
    } = answers;
    // UPDATED LOGIC: If colorTheme is 'yes', recommend Fancy Tot or Mega Fancy Tot
    if (colorTheme === 'yes') {
      if (guestCount === '16-20' || spaceSize === 'large') {
        return {
          name: 'Mega Fancy Tot',
          description: 'Our most luxurious package with full color customization for your large event',
          reasons: ['Fully customizable colors and theme', 'Perfect for large groups (12-20 kids)', 'Luxury aesthetic with premium white base', 'Large bounce house and extensive play area'],
          link: '/luxurysoftplay',
          color: 'bg-purple-100 border-purple-300',
          packageValue: 'mega-fancy-tot',
          bookingType: 'softplay',
          price: '$525'
        };
      }
      return {
        name: 'Fancy Tot',
        description: 'Elegant luxury setup with your custom color theme',
        reasons: ['Fully customizable colors and theme', 'Clean, luxury white base design', 'Perfect for 10-15 kids', 'Sophisticated aesthetic for special celebrations'],
        link: '/luxurysoftplay',
        color: 'bg-purple-100 border-purple-300',
        packageValue: 'fancy-tot',
        bookingType: 'softplay',
        price: '$475'
      };
    }
    // Large group recommendations
    if (guestCount === '16-20') {
      if (aestheticImportance === 'very' || lookPreference === 'luxury') {
        return {
          name: 'Mega Fancy Tot',
          description: 'Luxury at scale - perfect for your large celebration',
          reasons: ['Accommodates 12-20 kids comfortably', 'Premium white aesthetic', 'Large white combo bounce house', 'Extensive play area for big groups'],
          link: '/luxurysoftplay',
          color: 'bg-purple-100 border-purple-300',
          packageValue: 'mega-fancy-tot',
          bookingType: 'softplay',
          price: '$525'
        };
      }
      return {
        name: 'Gigantatot',
        description: 'Our biggest colorful package for maximum fun',
        reasons: ['Perfect for 15-20 kids', 'Two bounce houses for variety', 'Large open play space', 'High-energy party setup'],
        link: '/softplay',
        color: 'bg-blue-100 border-blue-300',
        packageValue: 'gigantatot',
        bookingType: 'softplay',
        price: '$500'
      };
    }
    // Luxury preference with medium/large space
    if ((aestheticImportance === 'very' || lookPreference === 'luxury') && spaceSize !== 'small') {
      if (guestCount === '11-15' || spaceSize === 'large') {
        return {
          name: 'Mega Fancy Tot',
          description: 'Elevated luxury for your special celebration',
          reasons: ['Premium white aesthetic', 'Perfect for 12-20 kids', 'Large white combo bounce house', 'Sophisticated and elegant'],
          link: '/luxurysoftplay',
          color: 'bg-purple-100 border-purple-300',
          packageValue: 'mega-fancy-tot',
          bookingType: 'softplay',
          price: '$525'
        };
      }
      return {
        name: 'Fancy Tot',
        description: 'Clean, luxury look for your mid-size party',
        reasons: ['Elegant all-white design', 'Perfect for 10-15 kids', 'Medium white bounce house with ball pit', 'Sophisticated aesthetic'],
        link: '/luxurysoftplay',
        color: 'bg-purple-100 border-purple-300',
        packageValue: 'fancy-tot',
        bookingType: 'softplay',
        price: '$475'
      };
    }
    // Small space or few kids
    if (spaceSize === 'small' || guestCount === '5-or-fewer') {
      return {
        name: 'Tiny Tot',
        description: 'Perfect for intimate gatherings and smaller spaces',
        reasons: ['Compact size for smaller spaces', 'Ideal for 5 or fewer kids', 'Fun and engaging for little ones', 'Easy setup in apartments or small rooms'],
        link: '/softplay',
        color: 'bg-green-100 border-green-300',
        packageValue: 'tiny-tot',
        bookingType: 'softplay',
        price: '$350'
      };
    }
    // Medium space with 6-10 kids
    if (guestCount === '6-10' && spaceSize === 'medium') {
      return {
        name: 'Mega Tot',
        description: 'Great variety and space for active play',
        reasons: ['Perfect for 6-10 kids', 'Combo bounce house with ball pit', 'Multiple play stations', 'Keeps kids engaged and rotating'],
        link: '/softplay',
        color: 'bg-blue-100 border-blue-300',
        packageValue: 'mega-tot',
        bookingType: 'softplay',
        price: '$425'
      };
    }
    // Default to Gigantatot for medium-sized parties
    if (guestCount === '11-15') {
      return {
        name: 'Gigantatot',
        description: 'Spacious setup for larger groups',
        reasons: ['Perfect for 11-15 kids', 'Two bounce houses', 'Large play area', 'High-energy fun'],
        link: '/softplay',
        color: 'bg-blue-100 border-blue-300',
        packageValue: 'gigantatot',
        bookingType: 'softplay',
        price: '$500'
      };
    }
    // Fallback
    return {
      name: 'Mega Tot',
      description: 'Our most popular package - great for most parties',
      reasons: ['Versatile for most party sizes', 'Combo bounce house with ball pit', 'Multiple play activities', 'Great value and variety'],
      link: '/softplay',
      color: 'bg-blue-100 border-blue-300',
      packageValue: 'mega-tot',
      bookingType: 'softplay',
      price: '$425'
    };
  };
  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      const result = calculateRecommendation();
      setRecommendation(result);
    }
  };
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  const handleAddToCart = () => {
    if (recommendation) {
      const result = addToCart({
        type: recommendation.bookingType as 'softplay' | 'bouncehouse',
        packageName: recommendation.name,
        packageValue: recommendation.packageValue,
        price: recommendation.price
      });
      if (result.success) {
        navigate('/cart');
      } else {
        alert(result.message || 'Unable to add item to cart');
      }
    }
  };
  const resetQuiz = () => {
    setCurrentStep(0);
    setAnswers({
      ages: [],
      guestCount: '',
      spaceSize: '',
      lookPreference: '',
      aestheticImportance: '',
      colorTheme: ''
    });
    setRecommendation(null);
    setIsQuizOpen(false);
  };
  const currentQuestion = questions[currentStep];
  if (!isQuizOpen) {
    return <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-2xl p-8 text-center border-2 border-primary-200" data-id="element-390">
        <SparklesIcon className="h-12 w-12 text-primary-500 mx-auto mb-4" data-id="element-391" />
        <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-392">
          Not Sure Which Package to Choose?
        </h3>
        <p className="text-gray-600 mb-6 max-w-2xl mx-auto" data-id="element-393">
          Take our quick quiz and we'll recommend the perfect soft play package
          for your event based on your space, guest count, and style
          preferences.
        </p>
        <button onClick={() => setIsQuizOpen(true)} className="inline-flex items-center px-6 py-3 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-all shadow-md" data-id="element-394">
          Find My Perfect Package
          <ChevronRightIcon className="ml-2 h-5 w-5" data-id="element-395" />
        </button>
      </div>;
  }
  if (recommendation) {
    return <div className="bg-white rounded-2xl shadow-xl border-2 border-primary-200 overflow-hidden" data-id="element-396">
        <div className="bg-gradient-to-r from-primary-500 to-secondary-500 p-6 text-white text-center" data-id="element-397">
          <SparklesIcon className="h-12 w-12 mx-auto mb-3" data-id="element-398" />
          <h3 className="text-2xl font-bold" data-id="element-399">Your Perfect Package</h3>
        </div>
        <div className="p-8" data-id="element-400">
          <div className={`rounded-xl p-6 mb-6 border-2 ${recommendation.color}`} data-id="element-401">
            <h4 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-402">
              {recommendation.name}
            </h4>
            <p className="text-lg text-gray-700 mb-4" data-id="element-403">
              {recommendation.description}
            </p>
            <div className="space-y-2" data-id="element-404">
              <p className="font-semibold text-gray-900" data-id="element-405">
                Why this package is perfect for you:
              </p>
              <ul className="space-y-2" data-id="element-406">
                {recommendation.reasons.map((reason, idx) => <li key={idx} className="flex items-start" data-id="element-407">
                    <span className="text-green-500 mr-2 mt-1" data-id="element-408">✓</span>
                    <span className="text-gray-700" data-id="element-409">{reason}</span>
                  </li>)}
              </ul>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-410">
            <Link to={recommendation.link} className="inline-flex items-center justify-center px-6 py-3 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-all shadow-md" onClick={() => window.scrollTo(0, 0)} data-id="element-411">
              View Package Details
            </Link>
            <button onClick={handleAddToCart} className="inline-flex items-center justify-center px-6 py-3 bg-secondary-500 text-white rounded-lg font-medium hover:bg-secondary-600 transition-all shadow-md" data-id="element-412">
              Add to Cart
            </button>
            <button onClick={resetQuiz} className="inline-flex items-center justify-center px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all" data-id="element-413">
              Start Over
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="bg-white rounded-2xl shadow-xl border-2 border-primary-200 overflow-hidden" data-id="element-414">
      <div className="bg-gray-100 h-2" data-id="element-415">
        <div className="bg-primary-500 h-2 transition-all duration-300" style={{
        width: `${(currentStep + 1) / questions.length * 100}%`
      }} data-id="element-416" />
      </div>

      <div className="p-8" data-id="element-417">
        <div className="text-sm text-gray-500 mb-4" data-id="element-418">
          Question {currentStep + 1} of {questions.length}
        </div>

        <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-419">
          {currentQuestion.question}
        </h3>
        {currentQuestion.subtitle && <p className="text-gray-600 mb-6" data-id="element-420">{currentQuestion.subtitle}</p>}

        <div className="space-y-3 mb-8" data-id="element-421">
          {currentQuestion.options.map(option => {
          const isSelected = currentQuestion.type === 'multiple' ? (answers[currentQuestion.id as keyof QuizAnswers] as string[]).includes(option.value) : answers[currentQuestion.id as keyof QuizAnswers] === option.value;
          return <button key={option.value} onClick={() => currentQuestion.type === 'multiple' ? handleMultipleChoice(currentQuestion.id, option.value) : handleSingleChoice(currentQuestion.id, option.value)} className={`w-full text-left p-4 rounded-lg border-2 transition-all ${isSelected ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'}`} data-id="element-422">
                <div className="flex items-center" data-id="element-423">
                  <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center ${isSelected ? 'bg-primary-500 border-primary-500' : 'border-gray-300'}`} data-id="element-424">
                    {isSelected && <span className="text-white text-xs" data-id="element-425">✓</span>}
                  </div>
                  <span className="font-medium text-gray-900" data-id="element-426">
                    {option.label}
                  </span>
                </div>
              </button>;
        })}
        </div>

        <div className="flex justify-between" data-id="element-427">
          <button onClick={handleBack} disabled={currentStep === 0} className="inline-flex items-center px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-428">
            <ChevronLeftIcon className="mr-2 h-5 w-5" data-id="element-429" />
            Back
          </button>
          <button onClick={handleNext} disabled={!canProceed()} className="inline-flex items-center px-6 py-2 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md" data-id="element-430">
            {currentStep === questions.length - 1 ? 'Get Recommendation' : 'Next'}
            <ChevronRightIcon className="ml-2 h-5 w-5" data-id="element-431" />
          </button>
        </div>
      </div>
    </div>;
};